import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { ClientRow } from '../api/types';
import { Badge, StatusBadge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorBanner, LoadingBlock, WarningBanner } from '../components/Feedback';
import { Button, Checkbox, Field, Input, Textarea, Toggle } from '../components/Form';
import { Modal } from '../components/Modal';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { ago, money, num, text } from '../lib/format';

export function ClientsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Create form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState('0');
  const [rateLimit, setRateLimit] = useState('120');
  const [maxConcurrent, setMaxConcurrent] = useState('50');
  const [allowNegative, setAllowNegative] = useState(false);
  const [notes, setNotes] = useState('');
  const [createKey, setCreateKey] = useState(true);
  const [creating, setCreating] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    return `/admin-api/clients?${params.toString()}`;
  }, [page, limit, search]);

  const listQuery = useQuery<{ list: ClientRow[] }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const doCreate = async () => {
    setCreating(true);
    try {
      const result = await api.post<{ id: number; name: string; api_key: string | null }>('/admin-api/clients', {
        name: name.trim(),
        email: email.trim() || null,
        balance: Number(balance) || 0,
        rate_limit_per_min: Number(rateLimit) || 120,
        max_concurrent_jobs: Number(maxConcurrent) || 50,
        allow_negative: allowNegative,
        notes: notes.trim() || null,
        create_key: createKey,
      });
      toast.success('Клиент создан');
      setCreateOpen(false);
      setName(''); setEmail(''); setBalance('0'); setRateLimit('120'); setMaxConcurrent('50'); setAllowNegative(false); setNotes(''); setCreateKey(true);
      if (result.api_key) setCreatedKey(result.api_key);
      void invalidatePrefix(queryClient, '/admin-api/clients');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setCreating(false);
    }
  };

  const list = listQuery.data?.list ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Клиенты"
        actions={<Button variant="primary" onClick={() => setCreateOpen(true)}>Создать клиента</Button>}
      />

      <Card title="Поиск" bodyClassName="flex gap-2">
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(e.currentTarget.value.trim()); setPage(1); } }}
          placeholder="Имя или email"
          className="flex-1"
        />
        <Button variant="primary" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>Найти</Button>
        <Button variant="ghost" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>Сбросить</Button>
      </Card>

      <Card noPadding>
        {listQuery.error ? (
          <div className="p-3">
            <ErrorBanner message={listQuery.error.message} onRetry={() => listQuery.refetch()} />
            <div className="mt-2">
              <WarningBanner message="Если ошибка повторяется, возможно, сервер не может выполнить подзапрос по статистике клиентов (известная проблема с типами в SQL)." />
            </div>
          </div>
        ) : listQuery.isLoading && !list.length ? (
          <LoadingBlock />
        ) : list.length === 0 ? (
          <EmptyState title="Клиенты не найдены" />
        ) : (
          <DataTable<ClientRow>
            columns={[
              { key: 'name', header: 'Имя', render: (r) => (
                <Link to={`/clients/${r.id}`} className="text-sky-400 hover:underline">{r.name}</Link>
              ) },
              { key: 'status', header: 'Статус', width: '5rem', render: (r) => <StatusBadge kind="client" status={r.status} /> },
              { key: 'email', header: 'Email', hide: 'sm', render: (r) => text(r.email) },
              { key: 'balance', header: 'Баланс', align: 'right', render: (r) => <span className={r.balance < 0 ? 'text-red-400' : ''}>{money(r.balance)}</span> },
              { key: 'available', header: 'Доступно', align: 'right', hide: 'md', render: (r) => money(r.available) },
              { key: 'keys', header: 'Ключи', align: 'right', hide: 'sm', width: '4rem', render: (r) => num(r.keys) },
              { key: 'purchases_24h', header: 'Пок. 24ч', align: 'right', hide: 'lg', width: '4rem', render: (r) => num(r.purchases_24h) },
              { key: 'spend_24h', header: 'Расход 24ч', align: 'right', hide: 'xl', render: (r) => money(r.spend_24h) },
              { key: 'created', header: 'Создан', align: 'right', hide: 'xl', width: '7rem', render: (r) => ago(r.created_at) },
            ]}
            rows={list}
            rowKey={(r) => r.id}
            loading={listQuery.isFetching}
            minWidth="36rem"
          />
        )}
      </Card>

      {/* Create client modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Создать клиента"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</Button>
            <Button variant="primary" loading={creating} onClick={doCreate}>Создать</Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Имя" className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Название клиента" />
          </Field>
          <Field label="Email">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" />
          </Field>
          <Field label="Баланс ($)">
            <Input value={balance} onChange={(e) => setBalance(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Лимит запросов / мин">
            <Input value={rateLimit} onChange={(e) => setRateLimit(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="Макс. параллельных задач">
            <Input value={maxConcurrent} onChange={(e) => setMaxConcurrent(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="Заметки" className="sm:col-span-2">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex items-center gap-4 sm:col-span-2">
            <Toggle checked={allowNegative} onChange={setAllowNegative} label="Разрешить отрицательный баланс" />
            <Checkbox checked={createKey} onChange={setCreateKey} label="Создать API-ключ" />
          </div>
        </div>
      </Modal>

      {/* Created key modal */}
      <Modal
        open={createdKey !== null}
        onClose={() => setCreatedKey(null)}
        title="API-ключ создан"
        size="sm"
        footer={<Button variant="primary" onClick={() => setCreatedKey(null)}>Понятно</Button>}
      >
        <div className="space-y-3">
          <WarningBanner
            title="Ключ показан один раз"
            message="Сохраните ключ сейчас — повторно он не отображается. После закрытия окна ключ недоступен."
          />
          <div className="flex items-center gap-2">
            <Mono className="flex-1 break-all text-[11px]">{createdKey}</Mono>
            <CopyButton value={createdKey ?? ''} label="Копировать" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
